/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  SlackIdentityMapping,
  SlackIdentityMapping$inboundSchema,
} from "./slackidentitymapping.js";

/**
 * Mapping state, separate from source state and freshness.
 */
export const MappingStatus = {
  Unmapped: "unmapped",
  Mapped: "mapped",
  NeedsReview: "needs_review",
} as const;
/**
 * Mapping state, separate from source state and freshness.
 */
export type MappingStatus = OpenEnum<typeof MappingStatus>;

/**
 * Observed account type.
 */
export const MemberType = {
  Person: "person",
  Guest: "guest",
  SingleChannelGuest: "single_channel_guest",
  Bot: "bot",
  Unknown: "unknown",
} as const;
/**
 * Observed account type.
 */
export type MemberType = OpenEnum<typeof MemberType>;

/**
 * Observed account state.
 */
export const SlackDirectoryMemberStatus = {
  Active: "active",
  Deactivated: "deactivated",
  Invited: "invited",
  Unknown: "unknown",
} as const;
/**
 * Observed account state.
 */
export type SlackDirectoryMemberStatus = OpenEnum<
  typeof SlackDirectoryMemberStatus
>;

export type SlackDirectoryMember = {
  /**
   * Connection that observed this workspace member.
   */
  connectionId: string;
  /**
   * Optional observed display name.
   */
  displayName?: string | undefined;
  /**
   * Observed email; never evidence of a confirmed Speakeasy identity.
   */
  email?: string | undefined;
  /**
   * Durable membership ID.
   */
  id: string;
  /**
   * Latest published observation.
   */
  lastSeenAt: Date;
  mapping?: SlackIdentityMapping | undefined;
  /**
   * When the finding was first recorded.
   */
  mappingConflictDetectedAt?: Date | undefined;
  /**
   * Sticky directory review finding, cleared only by an admin decision.
   */
  mappingConflictReason?: string | undefined;
  /**
   * Version of admin mapping decisions, independent of directory sync.
   */
  mappingRevision: number;
  /**
   * Mapping state, separate from source state and freshness.
   */
  mappingStatus: MappingStatus;
  /**
   * Observed account type.
   */
  memberType: MemberType;
  /**
   * Opaque token for the directory evidence reviewed by the administrator.
   */
  observationToken: string;
  /**
   * Whether this row was present in its workspace's last complete snapshot.
   */
  observedInLastSync: boolean;
  /**
   * Slack workspace user ID.
   */
  slackUserId: string;
  /**
   * Observed account state.
   */
  status: SlackDirectoryMemberStatus;
  /**
   * Verified member workspace.
   */
  workspaceId: string;
  /**
   * Workspace display name.
   */
  workspaceName: string;
};

/** @internal */
export const MappingStatus$inboundSchema: z.ZodMiniType<
  MappingStatus,
  unknown
> = openEnums.inboundSchema(MappingStatus);

/** @internal */
export const MemberType$inboundSchema: z.ZodMiniType<MemberType, unknown> =
  openEnums.inboundSchema(MemberType);

/** @internal */
export const SlackDirectoryMemberStatus$inboundSchema: z.ZodMiniType<
  SlackDirectoryMemberStatus,
  unknown
> = openEnums.inboundSchema(SlackDirectoryMemberStatus);

/** @internal */
export const SlackDirectoryMember$inboundSchema: z.ZodMiniType<
  SlackDirectoryMember,
  unknown
> = z.pipe(
  z.object({
    connection_id: z.string(),
    display_name: z.optional(z.string()),
    email: z.optional(z.string()),
    id: z.string(),
    last_seen_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    mapping: z.optional(SlackIdentityMapping$inboundSchema),
    mapping_conflict_detected_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    mapping_conflict_reason: z.optional(z.string()),
    mapping_revision: z.int(),
    mapping_status: MappingStatus$inboundSchema,
    member_type: MemberType$inboundSchema,
    observation_token: z.string(),
    observed_in_last_sync: z.boolean(),
    slack_user_id: z.string(),
    status: SlackDirectoryMemberStatus$inboundSchema,
    workspace_id: z.string(),
    workspace_name: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "connection_id": "connectionId",
      "display_name": "displayName",
      "last_seen_at": "lastSeenAt",
      "mapping_conflict_detected_at": "mappingConflictDetectedAt",
      "mapping_conflict_reason": "mappingConflictReason",
      "mapping_revision": "mappingRevision",
      "mapping_status": "mappingStatus",
      "member_type": "memberType",
      "observation_token": "observationToken",
      "observed_in_last_sync": "observedInLastSync",
      "slack_user_id": "slackUserId",
      "workspace_id": "workspaceId",
      "workspace_name": "workspaceName",
    });
  }),
);

export function slackDirectoryMemberFromJSON(
  jsonString: string,
): SafeParseResult<SlackDirectoryMember, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SlackDirectoryMember$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SlackDirectoryMember' from JSON`,
  );
}
